export interface ProjectSelectionCandidate {
  projectId: string;
  teamProjectId: string;
}

interface ProjectStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export type ProjectStorageProvider = () => ProjectStorage;

const STORAGE_PREFIX = 'nimbalyst:web-console:last-project';

function storageKey(orgId: string): string {
  return `${STORAGE_PREFIX}:${encodeURIComponent(orgId)}`;
}

function matchesProjectId(project: ProjectSelectionCandidate, projectId: string): boolean {
  // Routes use the server-minted teamProjectId, while the stable projectId is
  // what we persist. Accepting both lets selection survive a project move that
  // replaces its teamProjectId without making an old preference authoritative.
  return project.teamProjectId === projectId || project.projectId === projectId;
}

export function getProjectRouteId(project: ProjectSelectionCandidate): string {
  return project.teamProjectId;
}

export function getProjectPersistenceId(project: ProjectSelectionCandidate): string {
  return project.projectId;
}

export function resolveProjectSelection<T extends ProjectSelectionCandidate>(
  projects: readonly T[],
  urlProjectId?: string | null,
  lastUsedProjectId?: string | null,
): T | null {
  // No projects means there is no authorized scope to invent, so callers must
  // render an empty/access state rather than manufacture a route.
  if (projects.length === 0) return null;

  // A sole visible project is unambiguous even when a bookmark or stored value
  // is stale, so it wins without requiring cleanup before the console can open.
  if (projects.length === 1) return projects[0]!;

  // A visible URL project wins for durable deep links and for Greg's intended
  // two-browser-tabs/two-projects workflow. An invisible URL is ignored without
  // distinguishing deleted from unauthorized projects.
  if (urlProjectId) {
    const fromUrl = projects.find(project => matchesProjectId(project, urlProjectId));
    if (fromUrl) return fromUrl;
  }

  // Deleted or newly inaccessible stored projects are ignored. First project
  // is the deterministic first-visit fallback so a multi-project org with no
  // history still opens instead of landing on a picker or a blank screen.
  if (lastUsedProjectId) {
    const lastUsed = projects.find(project => matchesProjectId(project, lastUsedProjectId));
    if (lastUsed) return lastUsed;
  }
  return projects[0]!;
}

function browserStorage(): ProjectStorage {
  return window.localStorage;
}

export function readLastUsedProjectId(
  orgId: string,
  getStorage: ProjectStorageProvider = browserStorage,
): string | null {
  try {
    const value = getStorage().getItem(storageKey(orgId));
    return value && value.length > 0 ? value : null;
  } catch {
    // Site data can be disabled; absence is the safe fallback.
    return null;
  }
}

export function rememberLastUsedProjectId(
  orgId: string,
  projectId: string,
  getStorage: ProjectStorageProvider = browserStorage,
): void {
  try {
    getStorage().setItem(storageKey(orgId), projectId);
  } catch {
    // Keep the in-memory selection usable when persistence is unavailable.
  }
}
